import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import { billingService } from '../../services/api';
import { INK, MUTED, RULE } from '../analytics/analyticsUi';

const INVOICE_STATUS = {
  paid: { label: 'Paid', color: '#1BAA52' },
  pending: { label: 'Awaiting payment', color: '#C77700' },
  failed: { label: 'Payment failed', color: '#C2410C' },
  cancelled: { label: 'Cancelled', color: MUTED }
};

const formatMoney = (amount, currency = 'INR') =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: currency || 'INR' }).format(amount || 0);

const formatDay = (value) =>
  value ? new Date(value).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : null;

const InvoiceDialog = ({ open, onClose, billingId, title }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !billingId) return;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await billingService.getInvoices();
        const forBilling = (response.data || []).filter((invoice) =>
          (typeof invoice.billing === 'object' && invoice.billing?._id === billingId) || invoice.billing === billingId
        );
        setInvoices(forBilling);
      } catch (err) {
        setError('Invoices could not be loaded. Try again in a moment.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [open, billingId]);

  const openPdf = (invoice) => {
    window.open(billingService.generatePdfUrl(invoice._id), '_blank', 'noopener');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="invoice-dialog-title">
      <DialogTitle id="invoice-dialog-title" sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
        <Box>
          <Typography component="span" sx={{ display: 'block', fontSize: '1.125rem', fontWeight: 600, color: INK }}>
            Invoices
          </Typography>
          {title && <Typography variant="body2" sx={{ color: MUTED }}>{title}</Typography>}
        </Box>
        <IconButton onClick={onClose} aria-label="Close invoices" size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pb: 3 }}>
        {loading ? (
          [0, 1, 2].map((i) => <Skeleton key={i} height={56} />)
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : !invoices.length ? (
          <Typography variant="body2" sx={{ color: MUTED, py: 2 }}>
            No invoices yet. An invoice is created when a payment for this subscription is confirmed.
          </Typography>
        ) : (
          <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
            {invoices.map((invoice) => {
              const status = INVOICE_STATUS[invoice.status] || { label: invoice.status, color: MUTED };
              const start = formatDay(invoice.billingPeriodStart);
              const end = formatDay(invoice.billingPeriodEnd);
              return (
                <Box
                  component="li"
                  key={invoice._id}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto',
                    gap: 2,
                    alignItems: 'center',
                    py: 1.75,
                    borderTop: `1px solid ${RULE}`
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontWeight: 600, color: INK, fontVariantNumeric: 'tabular-nums' }}>
                        {formatMoney(invoice.amount, invoice.currency)}
                      </Typography>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                        <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: status.color }} />
                        <Typography variant="body2" sx={{ color: INK }}>{status.label}</Typography>
                      </Box>
                    </Box>
                    <Typography variant="body2" sx={{ color: MUTED, fontVariantNumeric: 'tabular-nums' }}>
                      {invoice.invoiceNumber}
                      {start && end ? `, ${start} to ${end}` : ''}
                    </Typography>
                    {invoice.paidDate && (
                      <Typography variant="caption" sx={{ color: MUTED, display: 'block' }}>
                        Paid on {formatDay(invoice.paidDate)}
                      </Typography>
                    )}
                    {invoice.description && (
                      <Typography variant="caption" sx={{ color: MUTED, display: 'block' }}>{invoice.description}</Typography>
                    )}
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<PictureAsPdfOutlinedIcon fontSize="small" />}
                    onClick={() => openPdf(invoice)}
                    sx={{ textTransform: 'none' }}
                  >
                    PDF
                  </Button>
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default InvoiceDialog;
